import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useT } from '../../i18n';

export default function AdminTeams() {
  const t = useT();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', description: '', vertretung_erforderlich: false });
  const [error, setError] = useState('');
  const [neuesMitglied, setNeuesMitglied] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([api.get('/teams/'), api.get('/users/')]);
      setTeams(t.data);
      setUsers(u.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const run = async (fn) => {
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.teams.actionFailed'));
    }
  };

  const anlegen = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post('/teams/', form);
      setForm({ name: '', description: '', vertretung_erforderlich: false });
    });
  };

  const loeschen = (team) => {
    if (window.confirm(t('admin.teams.confirmDelete', { name: team.name }))) run(() => api.delete(`/teams/${team.id}`));
  };

  const hinzufuegen = (team) => {
    const userId = neuesMitglied[team.id];
    if (!userId) return;
    run(async () => {
      await api.post(`/teams/${team.id}/members`, { user_id: Number(userId) });
      setNeuesMitglied({ ...neuesMitglied, [team.id]: '' });
    });
  };

  if (loading) return <div className="text-center"><div className="spinner-border" /></div>;

  return (
    <div>
      <h4 className="mb-4"><i className="bi bi-people me-2" />{t('admin.teams.title')}</h4>

      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-title">{t('admin.teams.newTitle')}</h6>
          <form className="row g-2" onSubmit={anlegen}>
            <div className="col-md-4">
              <input className="form-control" placeholder={t('admin.teams.name')} required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="col-md-6">
              <input className="form-control" placeholder={t('admin.teams.description')} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary w-100">{t('admin.teams.create')}</button>
            </div>
            <div className="col-12">
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="neu-vertretung" checked={form.vertretung_erforderlich}
                  onChange={(e) => setForm({ ...form, vertretung_erforderlich: e.target.checked })} />
                <label className="form-check-label" htmlFor="neu-vertretung">{t('admin.teams.substituteRequired')}</label>
              </div>
            </div>
          </form>
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3">
        {teams.length === 0 && <div className="text-muted">{t('admin.teams.empty')}</div>}
        {teams.map((team) => (
          <div className="col-md-6" key={team.id}>
            <div className="card h-100">
              <div className="card-header d-flex align-items-center">
                <strong>{team.name}</strong>
                {team.kann_verwalten && (
                  <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => loeschen(team)} title={t('admin.teams.deleteTitle')}>
                    <i className="bi bi-trash" />
                  </button>
                )}
              </div>
              <ul className="list-group list-group-flush">
                {team.description && <li className="list-group-item text-muted small">{team.description}</li>}
                <li className="list-group-item">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id={`vertretung-${team.id}`}
                      checked={team.vertretung_erforderlich} disabled={!team.kann_verwalten}
                      onChange={(e) => run(() => api.put(`/teams/${team.id}`, { vertretung_erforderlich: e.target.checked }))} />
                    <label className="form-check-label" htmlFor={`vertretung-${team.id}`}>{t('admin.teams.substituteRequired')}</label>
                  </div>
                  <small className="text-muted">{t('admin.teams.substituteRequiredHint')}</small>
                </li>
                {team.members.length === 0 && <li className="list-group-item text-muted">{t('admin.teams.noMembers')}</li>}
                {team.members.map((m) => (
                  <li className="list-group-item d-flex align-items-center" key={m.user_id}>
                    {m.name} <small className="text-muted ms-2">{m.personnel_number}</small>
                    {team.kann_verwalten && (
                      <button className="btn btn-sm btn-outline-secondary ms-auto"
                        onClick={() => run(() => api.delete(`/teams/${team.id}/members/${m.user_id}`))}
                        title={t('admin.teams.removeTitle')}><i className="bi bi-x-lg" /></button>
                    )}
                  </li>
                ))}
              </ul>
              {team.kann_verwalten && (
                <div className="card-footer">
                  <div className="input-group input-group-sm">
                    <select className="form-select" value={neuesMitglied[team.id] || ''}
                      onChange={(e) => setNeuesMitglied({ ...neuesMitglied, [team.id]: e.target.value })}>
                      <option value="">{t('admin.teams.addEmployee')}</option>
                      {users
                        .filter((u) => !team.members.some((m) => m.user_id === u.id))
                        .map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                    </select>
                    <button className="btn btn-primary" onClick={() => hinzufuegen(team)}>{t('admin.teams.add')}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
